// @ts-check
/** @typedef {import('../types/api').Influencer} Influencer */
/** @typedef {import('../types/api').PaginatedInfluencers} PaginatedInfluencers */
/** @typedef {import('../types/api').Dress} Dress */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const influencerService = {
  /**
   * @param {Object} [filters]
   * @returns {Promise<PaginatedInfluencers>}
   */
  getInfluencers: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.outreach_status) params.append('outreach_status', filters.outreach_status);
    if (filters.country) params.append('country', filters.country);
    if (filters.payment_status) params.append('payment_status', filters.payment_status);
    if (filters.collab_status) params.append('collab_status', filters.collab_status);
    if (filters.search) params.append('search', filters.search);
    const qs = params.toString();
    const data = await api.get(`/api/admin/influencers${qs ? `?${qs}` : ''}`);
    assertShape(data, ['items', 'total'], 'influencerService.getInfluencers');
    return data;
  },

  /**
   * @param {string} id
   * @returns {Promise<Influencer>}
   */
  getInfluencer: async (id) => {
    const data = await api.get(`/api/admin/influencers/${id}`);
    assertShape(data, ['id'], 'influencerService.getInfluencer');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<Influencer>}
   */
  createInfluencer: (data) => api.post('/api/admin/influencers', data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<Influencer>}
   */
  updateInfluencer: (id, data) => api.put(`/api/admin/influencers/${id}`, data),

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteInfluencer: (id) => api.del(`/api/admin/influencers/${id}`),

  /**
   * @param {string} id
   * @param {string} status
   * @returns {Promise<Influencer>}
   */
  updateStatus: (id, status) => api.patch(`/api/admin/influencers/${id}/status`, { outreach_status: status }),

  /**
   * Look up a public Instagram profile via Business Discovery API.
   * @param {string} username
   */
  lookupIgProfile: async (username) => {
    const clean = username.replace('@', '');
    return api.get(`/api/admin/influencers/ig-lookup?username=${encodeURIComponent(clean)}`);
  },

  /**
   * @param {string} id
   * @returns {Promise<Dress[]>}
   */
  getDresses: async (id) => {
    const data = await api.get(`/api/admin/influencers/${id}/dresses`);
    assertArray(data, 'influencerService.getDresses');
    return data;
  },
};

export default influencerService;
